/**
 * @author: Pawan Singh Sisodiya
 */
import React, { useState, useEffect, useRef } from "react";
import { Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import FeeReceipt from "./FeeReceipt.jsx";
import "../../App.css";
const parseSafe = (val) => {
  if (typeof val === "string") return parseInt(val.replace(/,/g, "")) || 0;
  return parseInt(val) || 0;
};

const InstallmentTable = ({
  data,
  edit,
  feeDepositHandler,
  readonly,
  feeDeposit, // Adding these back to support the shared calculation logic
  studentRecord,
}) => {
  const [installments, setInstallments] = useState([]);
  const [headsArray, setHeadsArray] = useState([]);
  const [indexValues, setIndexValues] = useState([]);
  const [flag, setFlag] = useState(0);
  const [latejoineediscount, setLateJoineeDiscount] = useState(0);

  const [selectedReceiptData, setSelectedReceiptData] = useState(null);
  const [isReceiptReady, setIsReceiptReady] = useState(false);
  const receiptRef = useRef();
  edit = edit === undefined ? false : edit;
  const currentReceiptLateFee = parseSafe(studentRecord?.late_fee);
  const getCurrentReceiptLateFeeItemId = (items) => {
    const currentReceiptInstallments = items.filter((item) => {
      if (!studentRecord?.deposit_id) return false;
      const depositIds = Array.isArray(item.deposit_id)
        ? item.deposit_id.map((id) => String(id))
        : String(item.deposit_id || "").split(",");
      const receiptDepositIds = Array.isArray(studentRecord.deposit_id)
        ? studentRecord.deposit_id.map((id) => String(id))
        : String(studentRecord.deposit_id).split(",");
      return depositIds.some((id) => receiptDepositIds.includes(id.trim()));
    });
    return currentReceiptInstallments.length
      ? currentReceiptInstallments[currentReceiptInstallments.length - 1].id
      : null;
  };
  useEffect(() => {
    if (data) {
      const sortedData = [...data].sort((a, b) => {
        const orderA = a.orderno !== undefined && a.orderno !== null ? a.orderno : Infinity;
        const orderB = b.orderno !== undefined && b.orderno !== null ? b.orderno : Infinity;

        if (orderA !== orderB && orderA !== Infinity && orderB !== Infinity) {
          return orderA - orderB;
        }

        // Fallback to due_date sorting
        const dateA = a.due_date ? new Date(a.due_date) : new Date(0);
        const dateB = b.due_date ? new Date(b.due_date) : new Date(0);
        return dateA - dateB;
      });
      setInstallments(sortedData);
    }
  }, [data]);

  useEffect(() => {
    const headsSet = new Set();
    setLateJoineeDiscount(0);

    const currentReceiptLateFeeItemId = getCurrentReceiptLateFeeItemId(installments);




    const checkedInstallments = installments.filter((item) => item.isCheked);
    const extraManualDiscount = parseSafe(feeDeposit?.currentDiscount || 0);
    const extraManualSharePerItem = (checkedInstallments.length > 0 && extraManualDiscount !== 0)
      ? (extraManualDiscount / checkedInstallments.length)
      : 0;

    installments.forEach((item, index) => {
      let netPayableForThisItem = 0;
      let grossPayableForThisItem = 0;

      item?.heads?.forEach((head) => {
        headsSet.add(head.headname);
      });

      // Keep the saved per-installment discount visible for fresh or completed rows.
      // For pending partial payments where preset discount was already consumed in previous receipts,
      // discount is 0 unless user adds a new manual discount in footer.
      const itemDepIds = Array.isArray(item.deposit_id)
        ? item.deposit_id
        : (item.deposit_id?.toString() || "").split(",").map((s) => s.trim()).filter(Boolean);

      const hasPriorDeposit = parseSafe(item.deposited_amount || 0) > 0 || itemDepIds.length > 0;
      const isPartialPending = item.status !== "completed" && hasPriorDeposit;
      const savedInstallmentDiscount = parseSafe(item.installment_discount || 0);

      const totalDiscount = item.isCheked
        ? Math.max(0, savedInstallmentDiscount + extraManualSharePerItem)
        : savedInstallmentDiscount;

      grossPayableForThisItem =
        parseSafe(item.installment_amount) +
        parseSafe(item.transport_fee ?? 0) -
        parseSafe(totalDiscount);

      // Use the exact previous_due coming from the backend response
      const displayPreviousDue = parseSafe(item.previous_due ?? 0);

      const fallbackLateFee = item.id === currentReceiptLateFeeItemId
        ? currentReceiptLateFee
        : 0;
      const isCompleted = item.status === "completed";
      const totalLateFeeLiability = isCompleted
        ? parseSafe(item.pushed_late_fee ?? item.late_fee_paid ?? 0)
        : Math.max(
            parseSafe(item.pushed_late_fee ?? 0),
            parseSafe(item.late_fee ?? 0),
            fallbackLateFee
          );
      const lateFeePaid = Math.max(
        parseSafe(item.late_fee_paid ?? 0),
        item.id === currentReceiptLateFeeItemId ? currentReceiptLateFee : 0
      );
      netPayableForThisItem =
        grossPayableForThisItem +
        displayPreviousDue +
        totalLateFeeLiability;

      const rawDeposited = parseSafe(item.deposited_amount ?? 0) + lateFeePaid;
      const depositedAmountForThisItem = rawDeposited;

      const pendingAmountForThisItem = isCompleted
        ? 0
        : Math.max(0, netPayableForThisItem - depositedAmountForThisItem);
      const isFullyPaid =
        depositedAmountForThisItem > 0 &&
        depositedAmountForThisItem >= netPayableForThisItem &&
        pendingAmountForThisItem === 0;

      item.net_payable_amount = netPayableForThisItem;
      item.discounted_amount = totalDiscount;
      item.display_previous_due = displayPreviousDue;
      item.grossPayableAmount = grossPayableForThisItem;
      item.pending_amount = pendingAmountForThisItem;
      item.display_deposited_amount = depositedAmountForThisItem;
      item.isFullyPaid = isFullyPaid;

      if (item.late_joinee) {
        setLateJoineeDiscount(
          (prev) => prev + item.installment_amount + item.transport_fee
        );
      }

      if (edit && (item.status === "completed" || isFullyPaid)) {
        if (item?.edit) {
          item.isdissable = false;
          setIndexValues((previndex) => {
            return [...previndex, { index: index }];
          });
        } else {
          item.isdissable = item.isdissable === undefined ? true : item.isdissable;
        }
      } else {
        if (!edit && index === 0 && item.status !== "completed") {
          item.isdissable = false;
        } else if (!edit && index > 0 &&
          (installments[index - 1].status === "completed" || installments[index - 1].isFullyPaid || installments[index - 1].isCheked) &&
          installments[index].status === "pending"
        ) {
          item.isdissable = false;
        } else if (item.promotedstatus !== null && item.promotedstatus !== "active") {
          item.isdissable = true;
        } else {
          item.isdissable = item.isdissable === undefined ? true : item.isdissable;
        }
      }
    });

    let dissable = installments?.filter(
      (vl) => vl?.status !== "completed" && vl?.late_joinee === false
    )[0];

    if (dissable && !edit) {
      dissable.isdissable = false;
    }

    // Allow re-selection of partially paid installments
    installments.forEach(item => {
      if (!item.isFullyPaid && (item.status === 'partial' || (item.status === 'pending' && parseSafe(item.deposited_amount) > 0))) {
        item.isdissable = false;
      }
    });
    console.log("dissable", dissable);

    headsSet.add("Transport Fee");
    headsSet.add("Total Amount");
    headsSet.add("Discount");
    headsSet.add("Gross Payable");
    headsSet.add("Previous Due");
    headsSet.add("Late Fee (Charged)");
    headsSet.add("Late Fee (Paid)");
    headsSet.add("Net Payable Amount");
    headsSet.add("Deposited Amount");
    headsSet.add("Pending Amount");
    headsSet.add("Receipt Number");

    setHeadsArray(Array.from(headsSet));
  }, [installments]);

  useEffect(() => {
    const selectedInstallments = [];
    if (indexValues.length) {
      if (flag <= 0) {
        selectedInstallments.push(
          installments.filter((res, index) =>
            indexValues.some((i) => i.index === index)
          )
        );
      }

      if (selectedInstallments.length) {
        feeDepositHandler(selectedInstallments[0]);
      }
    }
  }, [indexValues]);

  const getFee = (item, headName, headFee) => {
    const currentReceiptLateFeeItemId = getCurrentReceiptLateFeeItemId(installments);
    switch (headName) {
      case "Transport Fee":
        return formatNumberWithCommas(item.transport_fee);
      case "Previous Due":
        return formatNumberWithCommas(item?.display_previous_due) ?? 0;
      case "Gross Payable":
        return formatNumberWithCommas(item?.grossPayableAmount) ?? 0;
      case "Net Payable Amount":
        return formatNumberWithCommas(item?.net_payable_amount) ?? 0;
      case "Deposited Amount":
        return formatNumberWithCommas(
          item?.display_deposited_amount !== undefined
            ? item.display_deposited_amount
            : (parseSafe(item?.deposited_amount || 0) + parseSafe(item?.late_fee_paid || 0))
        ) ?? 0;
      case "Pending Amount":
        return formatNumberWithCommas(item?.pending_amount) ?? 0;
      case "Discount":
        return formatNumberWithCommas(item?.discounted_amount) ?? 0;
      case "Total Amount":
        return formatNumberWithCommas(
          item.installment_amount + item.transport_fee
        );
      case "Late Fee (Charged)":
        const isCompletedItem = item.status === "completed";
        const charged = isCompletedItem
          ? parseSafe(item.pushed_late_fee ?? item.late_fee_paid ?? 0)
          : Math.max(
              parseSafe(item.pushed_late_fee ?? 0),
              parseSafe(item.late_fee ?? 0),
              item.id === currentReceiptLateFeeItemId ? currentReceiptLateFee : 0
            );
        return charged ? formatNumberWithCommas(charged) : 0;
      case "Late Fee (Paid)":
        return formatNumberWithCommas(
          Math.max(
            parseSafe(item.late_fee_paid ?? 0),
            item.id === currentReceiptLateFeeItemId ? currentReceiptLateFee : 0
          )
        );
      case "Receipt Number":
        if (!item.receipt_number) return "NA";
        
        const receiptNumbers = item.receipt_number.toString().split(',').map(s => s.trim());
        const depositIds = Array.isArray(item.deposit_id) 
          ? item.deposit_id 
          : (item.deposit_id?.toString() || "").split(',').map(s => s.trim());

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
            {receiptNumbers.map((num, idx) => {
              const specificId = depositIds[idx] || depositIds[0]; // Fallback to first if mismatch
              const row = { ...item, deposit_id: specificId };
              return (
                <Link 
                  key={idx} 
                  to="#" 
                  style={{ fontSize: '11px', textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrintClick(row);
                  }}
                >
                  {num}
                </Link>
              );
            })}
          </div>
        );
      default:
        return headFee;
    }
  };

  const handleChange = (e, month, index) => {
    const { checked } = e.target;
    setInstallments((prevInstallments) => {
      const updatedInstallments = [...prevInstallments];
      const selectedInstallments = [];
      updatedInstallments[index].isCheked = checked;

      if (edit) {
        if (checked) {
          if (updatedInstallments[index + 1]?.edit) {
            updatedInstallments[index + 1].isdissable = false;
          }
          updatedInstallments[index].status = "completed";
        } else {
          updatedInstallments[index].status = "pending";
          for (var i = index + 1; i < updatedInstallments.length; i++) {
            updatedInstallments[i].status = "pending";
            updatedInstallments[i].isCheked = checked;
            updatedInstallments[i].isdissable = true;
          }
        }

        selectedInstallments.push(
          updatedInstallments.filter(
            (res) =>
              res.edit && res.isdissable === false && res.status === "completed"
          )
        );
        setFlag(flag + 1);
      } else {
        if (checked) {
          if (index + 1 < updatedInstallments.length) {
            updatedInstallments[index + 1].isdissable = false;
          }
        } else {
          for (var i = index + 1; i < updatedInstallments.length; i++) {
            updatedInstallments[i].isCheked = checked;
            updatedInstallments[i].isdissable = true;
          }
        }
        selectedInstallments.push(
          updatedInstallments.filter((res) => res.isCheked === true)
        );
      }

      let dissable = installments?.filter((vl) => vl?.late_joinee === false)[0];
      if (
        selectedInstallments[0].length > 0 &&
        dissable?.id === selectedInstallments[0][0]?.id
      ) {
        selectedInstallments[0][0].late_joinee_discount = latejoineediscount;
      }
      feeDepositHandler(selectedInstallments[0]);
      return updatedInstallments;
    });
  };

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    documentTitle: "FeeReceipt",
    onAfterPrint: () => {
      setIsReceiptReady(false);
      setSelectedReceiptData(null);
    },
  });

  const handlePrintClick = (data) => {
    setIsReceiptReady(false);
    setSelectedReceiptData(data);
  };

  useEffect(() => {
    if (selectedReceiptData && isReceiptReady) {
      handlePrint();
    }
  }, [selectedReceiptData, isReceiptReady, handlePrint]);

  const formatNumberWithCommas = (number) => {
    return number ? number.toLocaleString("en-IN") : "0";
  };



  return (
    <div style={{ overflowY: "auto" }}>
      <Table striped bordered hover id="myTable">
        <thead style={{ marginLeft: "100px", width: "90px" }}>
          <tr>
            <th
              style={{
                whiteSpace: "nowrap",
                backgroundColor: "#637384",
                color: "white",
                textAlign: "center",
              }}
            >
              Heads
            </th>
            {installments.map((item, index) => (
              <th
                style={{
                  whiteSpace: "nowrap",
                  backgroundColor: "#637384",
                  color: "white",
                  textAlign: "center",
                }}
                key={item.id}
              >
                {item.month}
                <Form>
                  <Form.Check
                    inline
                    type="checkbox"
                    name={"checkedMonth"}
                    checked={
                      item?.status === "completed" ||
                      item?.isFullyPaid ||
                      item?.isCheked
                    }
                    onChange={(e) => handleChange(e, item.month, index)}
                    disabled={
                      readonly ||
                      item?.late_joinee ||
                      item?.isFullyPaid ||
                      (!item.edit &&
                        (item?.isdissable || item?.status === "completed"))
                    }
                    className={item.late_joinee ? "late-joinee" : ""}
                  />
                </Form>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ width: "140px" }}>
          {console.log("headarray", headsArray)}
          {headsArray.map((headName) => (
            <tr key={headName}>
              <td>{headName}</td>
              {installments.map((item) => {
                const head = item.heads?.find((h) => h.headname === headName);
                const headFee = head ? head.head_amount : 0;
                return (
                  <td key={item.id} className="text-end">
                    {getFee(item, headName, formatNumberWithCommas(headFee))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
      {selectedReceiptData && (
        <div style={{ display: "none" }}>
          <FeeReceipt
            ref={receiptRef}
            receiptData={selectedReceiptData}
            onReady={() => setIsReceiptReady(true)}
          />
        </div>
      )}
    </div>
  );
};

export default InstallmentTable;
